/**
 * Inline markup renderer.
 *
 * Turns the token list from `parseInline()` into React elements. It is the only
 * bridge between author-typed marks and the DOM, and it crosses that bridge
 * with React children — never with `dangerouslySetInnerHTML`. That single
 * choice is what makes stored XSS impossible here: a paragraph containing
 * `<script>` renders as the five visible characters `<`, `s`, … and nothing
 * else, because React escapes text nodes and we never hand it raw HTML.
 *
 * Layer rule: this file names elements and semantic classes; it never says what
 * bold or a link *looks* like. That belongs to the theme.
 *
 * 行内标记渲染器。
 *
 * 把 parseInline() 产出的 token 列表变成 React 元素。它是「作者输入的标记」通往
 * DOM 的唯一桥梁，而过桥的方式只有 React children，绝不用
 * dangerouslySetInnerHTML。正是这一个选择让存储型 XSS 在这里不可能发生：段落里
 * 写 <script> 只会显示出这几个可见字符，因为 React 会转义文本节点，而我们从不
 * 交给它原始 HTML。
 *
 * 分层规则：本文件只决定用什么元素、什么语义类名；粗体和链接「长什么样」由主题
 * 决定。
 */

import { Fragment } from 'react'
import { isSafeUrl, parseInline } from '../lib/inline.js'
import { cx } from '../ui/index.js'

/**
 * True when a href leaves this site and therefore needs a hardened `rel`.
 *
 * `isSafeUrl()` also accepts relative, anchor and query links; those stay in the
 * current tab, so they must not get `target="_blank"` — opening an in-site link
 * in a new tab is an annoyance, not a security measure.
 *
 * 判断 href 是否离开本站，从而需要加固 rel。isSafeUrl() 同时接受相对路径、锚点和
 * 查询串，那些链接留在当前标签页，因此不该加 target="_blank"——站内链接开新标签
 * 只是打扰，并非安全措施。
 *
 * @param {string} href Candidate URL / 待判断链接。
 * @returns {boolean} True when external / 属于外链则为 true。
 */
function isExternalHref(href) {
  return /^(https?:|mailto:|tel:)/i.test(String(href || '').trim())
}

/**
 * Render one parsed token.
 *
 * 渲染单个 token。
 *
 * @param {{type: string, text: string, href?: string}} token Parsed token / 解析出的 token。
 * @param {number} key React key / React key。
 * @returns {JSX.Element} Element / 元素。
 */
function renderToken(token, key) {
  switch (token.type) {
    case 'bold':
      return (
        <strong className="inline__strong" key={key}>
          {token.text}
        </strong>
      )
    case 'italic':
      return (
        <em className="inline__em" key={key}>
          {token.text}
        </em>
      )
    case 'code':
      return (
        <code className="inline__code" key={key}>
          {token.text}
        </code>
      )
    case 'link': {
      // Belt and braces: parseInline already drops unsafe schemes, but this
      // component is exported and could be handed tokens from elsewhere.
      // 双保险：parseInline 已经丢弃了不安全协议，但本组件是导出的，
      // 也可能从别处拿到 token。
      if (!isSafeUrl(token.href)) return <Fragment key={key}>{token.text}</Fragment>
      const external = isExternalHref(token.href)
      return (
        <a
          key={key}
          className={cx('ui-link', 'inline__link', external && 'inline__link--external')}
          href={token.href}
          target={external ? '_blank' : undefined}
          rel={external ? 'noopener noreferrer' : undefined}
        >
          {token.text}
        </a>
      )
    }
    default:
      return <Fragment key={key}>{token.text}</Fragment>
  }
}

/**
 * Render a string of inline markup.
 *
 * Returns a fragment rather than a wrapper element so the caller decides the
 * containing tag — the same text is a `<p>` in a paragraph block, an `<li>` in
 * a list and a `<figcaption>` under an image.
 *
 * 渲染一段行内标记文本。返回 Fragment 而不是包裹元素，容器标签由调用方决定——
 * 同样一段文字在段落块里是 <p>，在列表里是 <li>，在图片下面是 <figcaption>。
 *
 * @param {object} props Props / 属性。
 * @param {string} props.text Source text with inline marks / 带行内标记的源文本。
 * @returns {JSX.Element} Fragment of inline elements / 行内元素片段。
 */
export function InlineText({ text }) {
  return <>{parseInline(text).map((token, index) => renderToken(token, index))}</>
}

export default InlineText
